import { Card, CardContent } from "@/components/ui/card";
import { Award, Briefcase, TrendingUp, CheckCircle, Star, Shield } from "lucide-react";
import janetHero from "@/assets/janet-hero.png";

const benefits = [
  {
    icon: Award,
    title: "Certificate of Completion",
    description: "Recognized certification upon graduation that validates your skills",
    color: "from-turquoise to-turquoise-dark",
  },
  {
    icon: Briefcase,
    title: "Job Referrals",
    description: "Connect with businesses actively looking for skilled social media managers",
    color: "from-maroon to-maroon-dark",
  },
  {
    icon: TrendingUp,
    title: "Professional Confidence",
    description: "Run ads and manage accounts like a seasoned professional",
    color: "from-turquoise-dark to-maroon",
  },
  {
    icon: CheckCircle,
    title: "Hands-On Experience",
    description: "Real campaigns, real results, real skills you can apply immediately",
    color: "from-maroon-dark to-turquoise",
  },
];

export function BenefitsSection() {
  return (
    <section className="relative py-12 md:py-16 lg:py-20 bg-gradient-to-b from-[hsl(0,100%,4%)] via-[hsl(0,90%,5%)] to-[hsl(0,100%,4%)] overflow-hidden">
      {/* Premium Background Pattern - Almost Black Maroon */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-radial from-maroon/8 via-transparent to-transparent blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-radial from-turquoise/8 via-transparent to-transparent blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="container relative z-10 px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          {/* Left - Content */}
          <div className="space-y-6 md:space-y-8 order-2 lg:order-1">
            {/* Header */}
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-turquoise/20 to-maroon/20 rounded-full border border-turquoise/30 mb-4 md:mb-6">
                <Star className="w-3 h-3 text-turquoise fill-turquoise" />
                <span className="text-xs font-semibold text-white">Beyond Just Training</span>
              </div>
              
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight text-white">
                What You'll
                <span className="block mt-1.5 md:mt-2 text-turquoise">
                  Actually Get
                </span>
              </h2>
              
              <p className="text-gray-300 text-sm md:text-base lg:text-lg leading-relaxed">
                Beyond just knowledge—tangible outcomes that launch your career and open doors to real opportunities.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid gap-3 md:gap-4">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                
                return (
                  <div
                    key={benefit.title}
                    className="group animate-slide-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <Card className="relative overflow-hidden bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] border border-white/10 hover:border-turquoise/50 transition-all duration-500 hover:shadow-2xl hover:shadow-turquoise/20">
                      {/* Hover gradient overlay */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${benefit.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                      
                      <CardContent className="p-4 md:p-6 lg:p-7 relative">
                        <div className="flex items-start gap-3 md:gap-4">
                          {/* Icon */}
                          <div className="relative flex-shrink-0 group-hover:animate-pulse-gentle">
                            <div className={`w-12 h-12 md:w-14 md:h-14 rounded-lg md:rounded-xl bg-gradient-to-br ${benefit.color} flex items-center justify-center shadow-lg`}>
                              <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" strokeWidth={2} />
                            </div>
                            {/* Glow effect */}
                            <div className={`absolute inset-0 w-12 h-12 md:w-14 md:h-14 rounded-lg md:rounded-xl bg-gradient-to-br ${benefit.color} blur-xl opacity-0 group-hover:opacity-60 transition-opacity duration-500`} />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading text-base md:text-lg lg:text-xl text-white mb-1 md:mb-2 font-bold">
                              {benefit.title}
                            </h3>
                            <p className="text-gray-300 text-xs md:text-sm lg:text-base leading-relaxed">
                              {benefit.description}
                            </p>
                          </div>

                          {/* Check icon */}
                          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-turquoise/20 border-2 border-turquoise/40 flex items-center justify-center">
                              <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-turquoise" />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>

            {/* Guarantee Badge */}
            <div className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/20 border border-emerald-500/30 rounded-xl md:rounded-2xl p-4 md:p-6">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-base md:text-lg mb-1.5 md:mb-2">Our Commitment to You</h4>
                  <p className="text-emerald-200 text-xs md:text-sm leading-relaxed">
                    We don't just teach—we connect you with opportunities. Janet personally refers qualified graduates to her network of businesses seeking skilled marketers.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Image */}
          <div className="relative order-1 lg:order-2 animate-fade-in">
            {/* Decorative elements */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-turquoise/15 to-maroon/15 rounded-3xl blur-2xl" />
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-gradient-to-tr from-maroon/15 to-turquoise/15 rounded-full blur-2xl" />
            
            {/* Main image container */}
            <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl border border-white/10">
              <img 
                src={janetHero} 
                alt="Janet Marketing Training Session" 
                className="w-full h-auto object-cover"
              />
              
              {/* Gradient overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(0,100%,4%)]/80 via-transparent to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-br from-turquoise/5 via-transparent to-maroon/5" />
              
              {/* Stats overlay */}
              <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 right-4 md:right-6">
                <div className="bg-black/60 backdrop-blur-md border border-white/10 rounded-lg md:rounded-xl p-3 md:p-4">
                  <div className="grid grid-cols-3 gap-2 md:gap-4 text-center">
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-white mb-0.5 md:mb-1">500+</div>
                      <div className="text-[10px] md:text-xs text-gray-300">Graduates</div>
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-white mb-0.5 md:mb-1">85%</div>
                      <div className="text-[10px] md:text-xs text-gray-300">Job Success</div>
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-white mb-0.5 md:mb-1">4.9★</div>
                      <div className="text-[10px] md:text-xs text-gray-300">Rating</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating accent cards */}
            <div className="absolute -right-4 top-1/4 hidden xl:block animate-float">
              <div className="bg-gradient-to-br from-turquoise to-turquoise-dark text-white px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl shadow-2xl border border-white/20">
                <div className="text-[10px] md:text-xs font-semibold mb-0.5 md:mb-1">Next Intake</div>
                <div className="text-base md:text-lg font-bold">Feb 17, 2026</div>
              </div>
            </div>
            
            <div className="absolute -left-4 bottom-1/3 hidden xl:block animate-float" style={{ animationDelay: '1s' }}>
              <div className="bg-gradient-to-br from-maroon to-maroon-dark text-white px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl shadow-2xl border border-white/20">
                <div className="text-[10px] md:text-xs font-semibold mb-0.5 md:mb-1">Training Mode</div>
                <div className="text-base md:text-lg font-bold">Hybrid</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }

        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.6s ease-out backwards;
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }

        @keyframes pulse-gentle {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
        .animate-pulse-gentle {
          animation: pulse-gentle 0.6s ease-in-out;
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}